import { useRef } from 'react'
import { useQuery } from 'react-query'
import { z } from 'zod'

import { queryAnalyticsV2Measure } from '_services/analytics-v2/analytics-v2.service'

import {
  ANALYTICS_V2_TRANSIENT_RETRY_COUNT,
  getAnalyticsV2RetryDelayMs,
  isColdCachePlaceholder,
  isTransientAnalyticsV2Error,
} from './analytics-v2.utils'

const ANALYTICS_V2_DEBUG_LOCAL_STORAGE_KEY = 'debug:analytics-v2' as const
const ANALYTICS_V2_DEBUG_ENABLED_VALUE = '1' as const
const DEFAULT_TRUST_TIER = 'verified' as const
const EMPTY_STRING = '' as const

const analyticsV2QueryFilterSchema = z.object({
  member: z.string(),
  operator: z.enum([
    'equals',
    'notEquals',
    'contains',
    'notContains',
    'startsWith',
    'endsWith',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
    'notIn',
    'set',
    'notSet',
    'inDateRange',
    'notInDateRange',
  ]),
  values: z.array(z.string()).optional(),
})
export type AnalyticsV2QueryFilter = z.infer<typeof analyticsV2QueryFilterSchema>

const analyticsV2TimeDimensionSchema = z.object({
  dimension: z.string(),
  granularity: z
    .enum(['second', 'minute', 'hour', 'day', 'week', 'month', 'quarter', 'year'])
    .optional(),
  dateRange: z.string().optional(),
})
export type AnalyticsV2TimeDimension = z.infer<typeof analyticsV2TimeDimensionSchema>

const analyticsV2OrderEntrySchema = z.object({
  member: z.string(),
  direction: z.enum(['asc', 'desc']),
})
export type AnalyticsV2OrderEntry = z.infer<typeof analyticsV2OrderEntrySchema>

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- OC-1030: consumed only through z.infer; no runtime validation wired yet
const analyticsV2QueryInputSchema = z.object({
  measures: z.array(z.string()),
  dimensions: z.array(z.string()).optional(),
  filters: z.array(analyticsV2QueryFilterSchema).optional(),
  timeDimensions: z.array(analyticsV2TimeDimensionSchema).optional(),
  segments: z.array(z.string()).optional(),
  limit: z.number().optional(),
  order: z.array(analyticsV2OrderEntrySchema).optional(),
})
export type AnalyticsV2QueryInput = z.infer<typeof analyticsV2QueryInputSchema>

const analyticsV2TransparencyBlockSchema = z.object({
  measureUsed: z.string(),
  dimensionsUsed: z.array(z.string()),
  scopeApplied: z.string(),
  timeRange: z.string().nullable(),
  dataFreshness: z.string(),
})
export type AnalyticsV2TransparencyBlock = z.infer<typeof analyticsV2TransparencyBlockSchema>

const analyticsV2TrustTierSchema = z.enum(['verified', 'preliminary', 'deprecated', 'unknown'])

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- OC-1030: consumed only through z.infer; no runtime validation wired yet
const analyticsV2QueryResponseSchema = z.object({
  rows: z.array(z.record(z.string(), z.custom<unknown>())),
  totalRows: z.number(),
  queryDurationMs: z.number(),
  trustTier: analyticsV2TrustTierSchema,
  transparency: analyticsV2TransparencyBlockSchema,
})
export type AnalyticsV2QueryResponse = z.infer<typeof analyticsV2QueryResponseSchema>

const rawTransparencySchema = z.object({
  measureUsed: z.string().nullish(),
  dimensionsUsed: z.array(z.string()).nullish(),
  scopeApplied: z.string().nullish(),
  timeRange: z.string().nullish(),
  dataFreshness: z.string().nullish(),
})

const rawResponseSchema = z.object({
  analyticsV2QueryMeasure: z.object({
    rows: z.array(z.unknown()).nullish(),
    totalRows: z.number().nullish(),
    queryDurationMs: z.number().nullish(),
    trustTier: z.string().nullish(),
    transparency: rawTransparencySchema.nullish(),
  }),
})

const isDebugEnabled = (): boolean =>
  typeof window !== 'undefined' &&
  window.localStorage?.getItem(ANALYTICS_V2_DEBUG_LOCAL_STORAGE_KEY) ===
    ANALYTICS_V2_DEBUG_ENABLED_VALUE

const normaliseRows = (
  rows: ReadonlyArray<unknown> | null | undefined
): Array<Record<string, unknown>> => {
  if (!rows) {
    return []
  }
  const out: Array<Record<string, unknown>> = []
  for (const row of rows) {
    if (row != null && typeof row === 'object') {
      out.push(row as Record<string, unknown>)
    }
  }
  return out
}

const normaliseResponse = (raw: unknown): AnalyticsV2QueryResponse => {
  const parsed = rawResponseSchema.parse(raw)
  const r = parsed.analyticsV2QueryMeasure
  const safeRows = normaliseRows(r.rows)
  const trustTierParsed = analyticsV2TrustTierSchema.safeParse(r.trustTier)
  return {
    rows: safeRows,
    totalRows: r.totalRows ?? 0,
    queryDurationMs: r.queryDurationMs ?? 0,
    trustTier: trustTierParsed.success ? trustTierParsed.data : DEFAULT_TRUST_TIER,
    transparency: {
      measureUsed: r.transparency?.measureUsed ?? EMPTY_STRING,
      dimensionsUsed: r.transparency?.dimensionsUsed ?? [],
      scopeApplied: r.transparency?.scopeApplied ?? EMPTY_STRING,
      timeRange: r.transparency?.timeRange ?? null,
      dataFreshness: r.transparency?.dataFreshness ?? EMPTY_STRING,
    },
  }
}

const COLD_CACHE_REFETCH_INTERVAL_MS = 4000
const COLD_CACHE_REFETCH_MAX_ATTEMPTS = 10

type AnalyticsV2RetryOptions = {
  retry: (failureCount: number, error: unknown) => boolean
  retryDelay: (failureCount: number) => number
}

/**
 * Returns the keys ABSENT unless a caller opts in, rather than passing
 * `undefined`. The query client's global `retry` is a deliberate never-retry
 * policy for the whole app, and react-query merges per-query options over the
 * defaults by spread — a present-but-undefined key would overwrite it.
 */
const buildRetryOptions = (
  retryTransientFailures: boolean | undefined
): AnalyticsV2RetryOptions | Record<string, never> => {
  if (retryTransientFailures !== true) {
    return {}
  }
  return {
    retry: (failureCount, error) =>
      failureCount < ANALYTICS_V2_TRANSIENT_RETRY_COUNT && isTransientAnalyticsV2Error(error),
    retryDelay: (failureCount) => getAnalyticsV2RetryDelayMs(failureCount, Math.random()),
  }
}

/**
 * Same reasoning as `buildRetryOptions`: the key is absent unless a caller opts
 * in, so a query that does not ask for it keeps whatever the client defaults to.
 */
const buildKeepPreviousDataOptions = (
  keepPreviousData: boolean | undefined
): { keepPreviousData: true } | Record<string, never> => {
  if (keepPreviousData !== true) {
    return {}
  }
  return { keepPreviousData: true }
}

export const useAnalyticsV2QueryMeasure = (
  input: AnalyticsV2QueryInput,
  skip = false,
  options?: {
    refetchWhileColdCache?: boolean
    retryTransientFailures?: boolean
    // Hold the last answer while a NEW query key is in flight, for a query whose
    // input changes in response to a click. Without it the snapshot drops to
    // null between the two keys, which every widget hook reads as "loading" and
    // every card draws as a skeleton — unmounting the control the reader just
    // used. Leave it off for a query keyed only on the page filters.
    keepPreviousData?: boolean
  }
) => {
  // The bridge's stale-if-slow cache mode answers cold queries with an empty
  // placeholder while BigQuery runs; opting in polls until real data lands
  // (bounded, so a genuinely empty/denied payload doesn't poll forever).
  const coldCacheAttemptsRef = useRef(0)
  const result = useQuery<AnalyticsV2QueryResponse>(
    [queryAnalyticsV2Measure.key, { input }],
    ({ queryKey }) =>
      queryAnalyticsV2Measure
        .query({ queryKey, meta: undefined, pageParam: undefined })
        .then((raw: unknown) => normaliseResponse(raw)),
    {
      enabled: !skip,
      refetchInterval: options?.refetchWhileColdCache
        ? (data) => {
            if (!isColdCachePlaceholder(data)) {
              coldCacheAttemptsRef.current = 0
              return false
            }
            if (coldCacheAttemptsRef.current >= COLD_CACHE_REFETCH_MAX_ATTEMPTS) {
              return false
            }
            coldCacheAttemptsRef.current += 1
            return COLD_CACHE_REFETCH_INTERVAL_MS
          }
        : undefined,
      ...buildRetryOptions(options?.retryTransientFailures),
      ...buildKeepPreviousDataOptions(options?.keepPreviousData),
    }
  )

  if (isDebugEnabled() && !result.isLoading) {
    // eslint-disable-next-line no-console
    console.log('[analytics-v2] query', {
      input,
      rowsLength: result.data?.rows.length,
      firstRow: result.data?.rows[0],
      totalRows: result.data?.totalRows,
      error: result.error,
    })
  }

  return {
    data: skip ? null : (result.data ?? null),
    isLoading: skip ? false : result.isLoading,
    error: skip ? null : result.error,
    refetch: () => result.refetch(),
  }
}
